(() => {
  const STORAGE_KEY = 'johgee.watering-plan.v1';
  const STORAGE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  const PUBLIC_STATUSES = [
    'WATER',
    'WAIT',
    'CHECK_SOIL',
    'RESTRICTED',
    'UNAVAILABLE',
  ];
  const HONEST_MODES = ['live', 'cached', 'stale', 'manual', 'unavailable'];

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => [
    ...root.querySelectorAll(selector),
  ];

  const emptyForm = () => ({
    garden_type: '',
    area_m2: '',
    container_count: '',
    soil_class: '',
    sun_exposure: 'partial_shade',
    establishment: '',
    irrigation_method: '',
    product_help: 'no_thanks',
    watering_zones: '',
    control_preference: 'either',
    water_source: 'unknown',
    tap_connection: 'unknown',
    wifi_2_4ghz: 'unknown',
    measured_flow_lpm: '',
    recent_rainfall: '',
    soil_feel: '',
    forecast_rainfall: '',
    postcode: '',
  });

  const readForm = (form) => {
    const values = emptyForm();
    for (const field of Object.keys(values)) {
      const input = form.elements.namedItem(field);
      if (input && 'value' in input) values[field] = String(input.value);
    }
    return values;
  };

  const writeForm = (form, values) => {
    for (const [field, value] of Object.entries(values)) {
      const input = form.elements.namedItem(field);
      if (input && 'value' in input) input.value = value;
    }
  };

  const validate = (values) => {
    const errors = {};
    if (!values.garden_type) errors.garden_type = 'Choose a garden type.';
    if (!values.soil_class) errors.soil_class = 'Choose a soil type.';
    if (!values.sun_exposure) errors.sun_exposure = 'Choose a sun level.';
    if (!values.establishment) {
      errors.establishment = 'Choose how established the planting is.';
    }
    if (!values.irrigation_method)
      errors.irrigation_method = 'Choose how you water.';
    if (values.product_help === 'match_me') {
      const zones = Number.parseInt(values.watering_zones, 10);
      if (!Number.isInteger(zones) || zones < 1 || zones > 8) {
        errors.watering_zones = 'Choose the number of watering areas.';
      }
    }
    if (!values.recent_rainfall)
      errors.recent_rainfall = 'Tell us about recent rain.';
    if (!values.soil_feel) errors.soil_feel = 'Tell us how the soil feels.';
    if (values.garden_type === 'containers') {
      const count = Number.parseInt(values.container_count, 10);
      if (!Number.isInteger(count) || count < 1 || count > 1000) {
        errors.container_count = 'Enter the number of containers (1–1000).';
      }
    } else {
      const area = Number.parseFloat(values.area_m2);
      if (!Number.isFinite(area) || area <= 0 || area > 100000) {
        errors.area_m2 = 'Enter the area in square metres.';
      }
    }
    if (values.measured_flow_lpm) {
      const flow = Number.parseFloat(values.measured_flow_lpm);
      if (!Number.isFinite(flow) || flow <= 0 || flow > 10000) {
        errors.measured_flow_lpm =
          'Enter flow in litres per minute, or leave blank.';
      }
    }
    if (values.postcode && values.postcode.trim()) {
      const compact = values.postcode.replaceAll(' ', '');
      if (!/^[A-Za-z][A-Za-z0-9]{3,6}[A-Za-z]$/u.test(compact)) {
        errors.postcode = 'Enter a valid UK postcode, or leave blank.';
      }
    }
    return errors;
  };

  const buildRequest = (values) => {
    const body = {
      garden_type: values.garden_type,
      soil_class: values.soil_class,
      sun_exposure: values.sun_exposure,
      establishment: values.establishment,
      irrigation_method: values.irrigation_method,
      product_help: values.product_help,
      control_preference: values.control_preference,
      water_source: values.water_source,
      tap_connection: values.tap_connection,
      wifi_2_4ghz: values.wifi_2_4ghz,
      site_id: 'johgee-uk',
      locale: 'en-GB',
      manual_context: {
        recent_rainfall: values.recent_rainfall,
        soil_feel: values.soil_feel,
      },
    };
    if (values.forecast_rainfall) {
      body.manual_context.forecast_rainfall = values.forecast_rainfall;
    }
    if (values.garden_type === 'containers') {
      body.container_count = Number.parseInt(values.container_count, 10);
    } else {
      body.area_m2 = Number.parseFloat(values.area_m2);
    }
    if (values.measured_flow_lpm) {
      body.measured_flow_lpm = Number.parseFloat(values.measured_flow_lpm);
    }
    if (values.product_help === 'match_me') {
      body.watering_zones = Number.parseInt(values.watering_zones, 10);
    }
    const postcode = values.postcode ? String(values.postcode).trim() : '';
    if (postcode) body.postcode = postcode;
    return body;
  };

  const loadSnapshot = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw || raw.toLowerCase().includes('postcode')) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.saved_at || !parsed.form) return null;
      if (Date.now() - Date.parse(parsed.saved_at) > STORAGE_TTL_MS)
        return null;
      return parsed.form;
    } catch {
      return null;
    }
  };

  const persistableForm = (values) => {
    const { postcode: _omitted, ...rest } = values;
    return rest;
  };

  const saveSnapshot = (values) => {
    const payload = {
      saved_at: new Date().toISOString(),
      form: persistableForm(values),
    };
    if (JSON.stringify(payload).toLowerCase().includes('postcode')) return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  };

  const publicStatus = (recommendation) => {
    if (PUBLIC_STATUSES.includes(recommendation?.status))
      return recommendation.status;
    return 'UNAVAILABLE';
  };

  const modeCopy = (dataMode) => {
    if (dataMode === 'live') {
      return 'Live weather for your area. Powered by Met Office data.';
    }
    if (dataMode === 'cached') {
      return 'Cached weather for your area (fresh enough to use). Powered by Met Office data.';
    }
    if (dataMode === 'stale') {
      return 'Stale cached weather. Check the soil; this is not a fresh reading.';
    }
    if (dataMode === 'manual') {
      return 'Manual mode, based on what you reported. Live weather was not used.';
    }
    return 'The data mode could not be confirmed.';
  };

  const restrictionCopy = (recommendation) => {
    const restriction = recommendation?.restriction || {};
    if (recommendation?.status === 'RESTRICTED') {
      return 'This watering method is restricted. A hosepipe or sprinkler must not be used while a ban is in force. This advice outranks any convenience to water.';
    }
    if (restriction.status === 'temporary_use_ban') {
      return 'A Temporary Use Ban is recorded for this water company. Hosepipes and sprinklers are not allowed.';
    }
    if (restriction.status === 'non_essential_use_ban') {
      return 'A Non-Essential Use Ban is recorded for this water company. Hosepipes and sprinklers are not allowed.';
    }
    if (restriction.status === 'none') {
      return 'No hosepipe ban is currently recorded for this water company. This is a decision aid, not legal advice.';
    }
    return 'Hosepipe-ban status could not be confirmed. Treat hosepipe and sprinkler advice as unavailable until you have checked your water company.';
  };

  const weatherCopy = (recommendation) => {
    const weather = recommendation?.weather_context || {};
    const parts = [];
    if (typeof weather.recent_rain_mm === 'number') {
      parts.push(
        `Recent rain credit ${weather.recent_rain_mm.toLocaleString('en-GB')} mm.`,
      );
    }
    if (typeof weather.forecast_rain_mm === 'number') {
      parts.push(
        `Forecast rain credit ${weather.forecast_rain_mm.toLocaleString('en-GB')} mm.`,
      );
    }
    if (weather.freshness) {
      parts.push(`Weather freshness: ${weather.freshness}.`);
    }
    if (weather.checked_at) {
      parts.push(`Checked ${weather.checked_at}.`);
    }
    if (weather.attribution) {
      parts.push(weather.attribution);
    }
    return parts.join(' ');
  };

  const litres = (value) =>
    typeof value === 'number' ? `${value.toLocaleString('en-GB')} L` : '—';
  const millimetres = (value) =>
    typeof value === 'number' ? `${value.toLocaleString('en-GB')} mm` : '—';
  const minutes = (value) =>
    typeof value === 'number'
      ? `${value.toLocaleString('en-GB')} min`
      : 'Not estimated';

  const money = (minor, currency) =>
    new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency || 'GBP',
    }).format((Number(minor) || 0) / 100);

  const mount = (root) => {
    const form = $('[data-johgee-form]', root);
    const live = $('[data-johgee-live]', root);
    const result = $('[data-johgee-result]', root);
    const banner = $('[data-johgee-banner]', root);
    const figures = $('[data-johgee-figures]', root);
    const summary = $('[data-johgee-summary]', root);
    const productsBox = $('[data-johgee-products]', root);
    const productsSummary = $('[data-johgee-products-summary]', root);
    const productList = $('[data-johgee-product-list]', root);
    const shareBox = $('[data-johgee-share]', root);
    const apiBase = (
      root.dataset.apiBase || 'https://api.johgeeirrig.co.uk'
    ).replace(/\/$/, '');
    let share = { token: null, revokeSecret: null };

    const recordEvent = (event) => {
      if (!event?.recommendation_id) return;
      fetch(`${apiBase}/v1/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(event),
        keepalive: true,
      }).catch(() => undefined);
    };

    const setStatus = (status, message) => {
      root.dataset.status = status;
      live.textContent = message;
      const busy = status === 'loading';
      form.setAttribute('aria-busy', busy ? 'true' : 'false');
      $$('button[type="submit"]', form).forEach((button) => {
        button.disabled = busy;
      });
    };

    const showErrors = (errors) => {
      $$('[data-error-for]', root).forEach((node) => {
        const field = node.getAttribute('data-error-for');
        const message = errors[field] || '';
        node.textContent = message;
        node.id = node.id || `johgee-error-${field}`;
        const input = form.elements.namedItem(field);
        if (input && 'setAttribute' in input) {
          if (message) {
            input.setAttribute('aria-invalid', 'true');
            input.setAttribute('aria-describedby', node.id);
          } else {
            input.removeAttribute('aria-invalid');
            input.removeAttribute('aria-describedby');
          }
        }
      });
    };

    const renderProducts = (recommendation, requested) => {
      if (!productsBox || !productsSummary || !productList) return;
      productList.replaceChildren();
      if (!requested) {
        productsBox.hidden = true;
        return;
      }

      productsBox.hidden = false;
      const products = Array.isArray(recommendation.products)
        ? recommendation.products
        : [];
      if (products.length === 0) {
        productsSummary.textContent =
          'No exact product is both compatible with these answers and available on the UK storefront today. We have not substituted a weaker match.';
        recordEvent({
          recommendation_id: recommendation.id,
          event_type: 'product_no_match_shown',
        });
        return;
      }

      productsSummary.textContent =
        'Matched from your answers, then checked against the live UK product page and stock status. Complete every check before ordering.';
      for (const product of products) {
        const article = document.createElement('article');
        article.className = 'johgee-planner__product';

        if (product.image_url) {
          const image = document.createElement('img');
          image.src = product.image_url;
          image.alt = '';
          image.loading = 'lazy';
          image.width = 320;
          image.height = 320;
          article.append(image);
        }

        const content = document.createElement('div');
        const heading = document.createElement('h3');
        heading.textContent = product.product_title;
        const price = document.createElement('p');
        price.className = 'johgee-planner__product-price';
        price.textContent = money(product.price_minor, product.currency);
        const reason = document.createElement('p');
        reason.textContent = product.reason;
        content.append(heading, price, reason);

        if (product.use_status === 'restriction_check_required') {
          const useWarning = document.createElement('p');
          useWarning.className = 'johgee-planner__product-use-warning';
          useWarning.textContent =
            'Hardware match only: today’s watering permission is not confirmed. Check your water company before using a hose, sprinkler or connected timer.';
          content.append(useWarning);
        }

        if (Array.isArray(product.checks) && product.checks.length > 0) {
          const checksHeading = document.createElement('p');
          checksHeading.className = 'johgee-planner__product-check-heading';
          checksHeading.textContent = 'Before you buy';
          const list = document.createElement('ul');
          for (const check of product.checks) {
            const item = document.createElement('li');
            item.textContent = check;
            list.append(item);
          }
          content.append(checksHeading, list);
        }

        const link = document.createElement('a');
        link.className = 'johgee-planner__product-link';
        link.href = product.product_url;
        link.textContent = 'Check product and current price';
        link.addEventListener('click', () => {
          recordEvent({
            recommendation_id: recommendation.id,
            event_type: 'product_clicked',
            product_handle: product.product_handle,
            rule_id: product.rule_id,
          });
        });
        content.append(link);
        article.append(content);
        productList.append(article);

        recordEvent({
          recommendation_id: recommendation.id,
          event_type: 'product_match_shown',
          product_handle: product.product_handle,
          rule_id: product.rule_id,
        });
      }
    };

    const renderResult = (recommendation, dataMode, values) => {
      const status = publicStatus(recommendation);
      const water = recommendation.water || {};
      banner.dataset.result = status;
      banner.textContent = status.replaceAll('_', ' ');
      summary.textContent = recommendation.explanation?.summary || '';
      figures.innerHTML = `
        <div class="johgee-planner__figure"><dt>Depth</dt><dd>${millimetres(water.depth_mm)}</dd></div>
        <div class="johgee-planner__figure"><dt>Litres per m²</dt><dd>${litres(water.litres_per_m2)}</dd></div>
        <div class="johgee-planner__figure"><dt>Total volume</dt><dd>${litres(water.total_litres)}</dd></div>
        <div class="johgee-planner__figure"><dt>Run time</dt><dd>${minutes(water.runtime_minutes)}</dd></div>
      `;
      $('[data-johgee-mode]', root).textContent = modeCopy(dataMode);
      const weatherNode = $('[data-johgee-weather]', root);
      if (weatherNode) weatherNode.textContent = weatherCopy(recommendation);
      const restrictionNode = $('[data-johgee-restriction]', root);
      if (restrictionNode) {
        restrictionNode.textContent = restrictionCopy(recommendation);
      }
      renderProducts(recommendation, values.product_help === 'match_me');
      result.hidden = false;
    };

    const saved = loadSnapshot();
    if (saved) writeForm(form, { ...emptyForm(), ...saved });

    const areaRow = $('[data-row="area_m2"]', root);
    const containerRow = $('[data-row="container_count"]', root);
    const syncSizeFields = () => {
      const containers =
        form.elements.namedItem('garden_type')?.value === 'containers';
      areaRow.hidden = containers;
      containerRow.hidden = !containers;
    };
    syncSizeFields();
    form.elements
      .namedItem('garden_type')
      ?.addEventListener('change', syncSizeFields);

    const productQuestions = $('[data-johgee-product-questions]', root);
    const syncProductFields = () => {
      const requested =
        form.elements.namedItem('product_help')?.value === 'match_me';
      if (productQuestions) productQuestions.hidden = !requested;
    };
    syncProductFields();
    form.elements
      .namedItem('product_help')
      ?.addEventListener('change', syncProductFields);

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const values = readForm(form);
      const errors = validate(values);
      showErrors(errors);
      if (Object.keys(errors).length > 0) {
        setStatus('validation', 'Please correct the highlighted answers.');
        result.hidden = true;
        form.querySelector('[aria-invalid="true"]')?.focus();
        return;
      }

      saveSnapshot(values);
      setStatus('loading', 'Working out a watering plan…');
      result.hidden = true;
      share = { token: null, revokeSecret: null };
      shareBox.hidden = true;

      if (!window.navigator.onLine) {
        setStatus(
          'offline',
          'You appear to be offline. Check the connection and try again.',
        );
        return;
      }

      try {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 20_000);
        const response = await fetch(`${apiBase}/v1/recommendations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(buildRequest(values)),
          signal: controller.signal,
        });
        window.clearTimeout(timeout);

        if (response.status === 429) {
          setStatus(
            'rate_limited',
            'Too many plans just now. Wait a minute and try again.',
          );
          return;
        }
        if (response.status === 422) {
          const problem = await response.json().catch(() => ({}));
          setStatus(
            'unprocessable',
            problem.detail ||
              'Some answers could not be used. Check the form and try again.',
          );
          return;
        }
        if (!response.ok) {
          setStatus(
            'api_error',
            'The watering service is unavailable. Try again shortly.',
          );
          return;
        }

        const recommendation = await response.json();
        const dataMode = response.headers.get('X-Johgee-Data-Mode');
        if (!HONEST_MODES.includes(dataMode) || dataMode === 'fixture') {
          setStatus(
            'api_error',
            'The service did not return an honest data mode.',
          );
          return;
        }
        renderResult(recommendation, dataMode, values);
        root.dataset.recommendationId = recommendation.id || '';
        recordEvent({
          recommendation_id: recommendation.id,
          event_type: 'plan_completed',
        });
        setStatus(
          'result',
          `Plan ready: ${publicStatus(recommendation).replaceAll('_', ' ')}.`,
        );
        $('[data-johgee-result]', root)?.focus?.();
      } catch (error) {
        if (!window.navigator.onLine || error?.name === 'AbortError') {
          setStatus(
            'offline',
            'The request timed out or the network dropped. Try again when the connection is steady.',
          );
          return;
        }
        setStatus('api_error', 'The watering service could not be reached.');
      }
    });

    $('[data-action="share"]', root)?.addEventListener('click', async () => {
      const recommendationId = root.dataset.recommendationId;
      if (!recommendationId) return;
      const response = await fetch(`${apiBase}/v1/share-plans`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recommendation_id: recommendationId }),
      });
      if (!response.ok) {
        setStatus('api_error', 'The share link could not be created.');
        return;
      }
      const created = await response.json();
      share = { token: created.token, revokeSecret: created.revoke_secret };
      const url = new URL(window.location.href);
      url.searchParams.set('share', created.token);
      $('[data-johgee-share-url]', root).value = url.toString();
      shareBox.hidden = false;
      setStatus(
        'result',
        'Share link created. Copy it now; the revoke secret is not shown again.',
      );
    });

    $('[data-action="copy-share"]', root)?.addEventListener(
      'click',
      async () => {
        const url = $('[data-johgee-share-url]', root).value;
        if (url) await navigator.clipboard.writeText(url);
      },
    );

    $('[data-action="revoke"]', root)?.addEventListener('click', async () => {
      if (!share.token || !share.revokeSecret) return;
      const response = await fetch(`${apiBase}/v1/share-plans/${share.token}`, {
        method: 'DELETE',
        headers: { 'X-Johgee-Revoke-Secret': share.revokeSecret },
      });
      if (response.status === 204 || response.status === 404) {
        share = { token: null, revokeSecret: null };
        shareBox.hidden = true;
        setStatus('result', 'The share link has been revoked.');
      }
    });

    $('[data-action="edit"]', root)?.addEventListener('click', () => {
      result.hidden = true;
      setStatus('form', 'Update the answers and calculate again.');
      form.querySelector('select, input')?.focus();
    });
  };

  const boot = () => {
    document.querySelectorAll('[data-johgee-planner]').forEach(mount);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
